import {
  AHQueryHandlerLoading,
  AHSeparator,
  Box,
  useLayoutContext,
  useTheme,
  useTodoSorter,
} from "arkh";
import { RouteProp, useFocusEffect } from "@react-navigation/core";
import { ActivityIndicator, ListRenderItem } from "react-native";

import { NativeStackNavigationProp } from "@react-navigation/native-stack";

import React, {
  Suspense,
  useCallback,
  useDeferredValue,
  useMemo,
  useState,
} from "react";
import { useSizeClass } from "../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import {
  useLastVisitedObjectAtTeam,
  useNotifyVisitedObjectAtLeaveNav,
} from "../../OS/VisitedTeam/LastVisitedTeamProvider";

import { FormattedMessage } from "react-intl";
import { UserLoggedInNavigatorParamList } from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { NoTodosComponent } from "./NoTodosComponent";
import {
  ConnectionHandler,
  graphql,
  useLazyLoadQuery,
  usePaginationFragment,
  useSubscription,
} from "react-relay";
import {
  TodoCategoryScreen_TodoCategoryTeam$data,
  TodoCategoryScreen_TodoCategoryTeam$key,
} from "./__generated__/TodoCategoryScreen_TodoCategoryTeam.graphql";

import { TodoCategoryScreenQuery } from "./__generated__/TodoCategoryScreenQuery.graphql";
import { TodoCategoryScreenTodoUpadateSubscription } from "./__generated__/TodoCategoryScreenTodoUpadateSubscription.graphql";
import { TodoCategoryScreenSubscription } from "./__generated__/TodoCategoryScreenSubscription.graphql";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { TodoCategoryScreenTodoDeleteSubscription } from "./__generated__/TodoCategoryScreenTodoDeleteSubscription.graphql";

import { useHotkeys } from "../../useHotKeys";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { useNavigationHeader } from "../../common/navigationHeaderOptions";
import { ActionFooter } from "@/src/components/ActionFooter";

import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import Animated from "react-native-reanimated";
import { defaultLayoutTransition } from "@/src/defaultLayoutTransition";
import { TodoListItem } from "@/src/components/TodoListItem";
import { TodoCategoryScreenFilter } from "./TodoCategoryScreenFilter";
import { CurrentTeamIdProvider } from "widgets/src/CurrentTeamIdContext";

const TODO_CATEGORY_SCREEN_QUERY = graphql`
  query TodoCategoryScreenQuery(
    $id: ID!
    $teamId: ID!
    $after: String
    $tagIds: [ID!]
    $assignedToMe: Boolean
    $assignedToEmployeeId: ID
    $employeeId: ID
    $first: Int
  ) {
    currentStore {
      myRole
      currentEmployee(id: $employeeId) {
        id
        ...TodoCategoryScreenFilter_employee
      }
    }
    todoCategory(id: $id) {
      id
      title
      integration
      tags {
        id
        title
        color
      }
      ...TodoCategoryScreenFilter_todoCategory
      team(id: $teamId) {
        id
        team {
          id
          name
          color
        }
        todoActiveAssignedCount(employeeId: $employeeId)
        ...TodoCategoryScreen_TodoCategoryTeam
      }
    }
  }
`;

type TodoCategoryScreenProps = {
  navigation: NativeStackNavigationProp<
    UserLoggedInNavigatorParamList,
    "TodoCategory"
  >;
  route: RouteProp<UserLoggedInNavigatorParamList, "TodoCategory">;
};

function TodoCategoryList(props: {
  team: TodoCategoryScreen_TodoCategoryTeam$key;

  teamId: string;
  todoCategoryId: string;
  openedAt: string;

  EmptyComponent: React.ReactElement;
  openTodo: (fromResponse: boolean, todoId: string) => void;
}) {
  const { space } = useLayoutContext();
  const insets = useSafeAreaInsets();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment TodoCategoryScreen_TodoCategoryTeam on TodoCategoryTeam
      @refetchable(queryName: "TodoCategoryTeamPagerQuery") {
        id
        todos(
          first: $first
          after: $after
          tagIds: $tagIds
          assignedToMe: $assignedToMe
          assignedToEmployeeId: $assignedToEmployeeId
        ) @connection(key: "TodoCategoryScreen_todos") {
          __id
          edges {
            node {
              id
              createdAtDate {
                isoString
              }
              ordinal
              ...TodoListItem_todo
            }
          }
        }
      }
    `,
    props.team
  );

  useSubscription<TodoCategoryScreenSubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription TodoCategoryScreenSubscription(
            $categoryId: ID!
            $teamId: ID!
            $connections: [ID!]!
          ) {
            todoCreatedNew(categoryId: $categoryId, teamIds: [$teamId]) {
              todo
                @prependNode(
                  connections: $connections
                  edgeTypeName: "TodoEdge"
                ) {
                id

                createdAtDate {
                  isoString
                }

                ordinal
                ...TodoListItem_todo
                category {
                  id
                  team(id: $teamId) {
                    id
                    notResolvedCount
                  }
                }
              }
            }
          }
        `,
        variables: {
          categoryId: props.todoCategoryId,
          teamId: props.teamId,
          connections: [data.todos.__id],
        },
      }),

      [data.todos.__id, props.teamId, props.todoCategoryId]
    )
  );

  useSubscription<TodoCategoryScreenTodoUpadateSubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription TodoCategoryScreenTodoUpadateSubscription(
            $categoryId: ID!
            $teamId: ID!
          ) {
            todoChanged(teamIds: [$teamId], categoryId: $categoryId) {
              ordinal
              ...TodoListItem_todo
            }
          }
        `,
        variables: {
          categoryId: props.todoCategoryId,
          teamId: props.teamId,
        },
      }),

      [props.teamId, props.todoCategoryId]
    )
  );

  useSubscription<TodoCategoryScreenTodoDeleteSubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription TodoCategoryScreenTodoDeleteSubscription(
            $categoryId: ID!
            $teamId: ID!
          ) {
            todoDeleted(categoryIds: [$categoryId], teamIds: [$teamId]) {
              id @deleteRecord
            }
          }
        `,
        variables: {
          categoryId: props.todoCategoryId,
          teamId: props.teamId,
        },
      }),

      [props.teamId, props.todoCategoryId]
    )
  );

  const lastVisited = useLastVisitedObjectAtTeam(
    "todoCategory",
    props.teamId,
    props.todoCategoryId
  );
  const todoSorter = useTodoSorter();
  const edges = useMemo(
    () =>
      data.todos.edges
        .filter((edge) => edge.node?.ordinal)
        .sort((a, b) => {
          return todoSorter(a?.node, b?.node);
        }),
    [data.todos.edges, todoSorter]
  );

  const renderItem: ListRenderItem<
    TodoCategoryScreen_TodoCategoryTeam$data["todos"]["edges"][0]
  > = useCallback(
    ({ item, index }) => {
      return (
        <TodoListItem
          todo={item.node}
          isFirst={index === 0}
          isLast={index === edges.length - 1}
          isNew={
            lastVisited === null ||
            (item.node.createdAtDate.isoString > lastVisited.toISOString() &&
              item.node.createdAtDate.isoString < props.openedAt)
          }
          openTodo={props.openTodo}
        />
      );
    },
    [edges.length, lastVisited, props.openTodo, props.openedAt]
  );

  const containerStyle = useMemo(
    () => ({
      paddingHorizontal: space.medium,
      paddingTop: space.medium,
      paddingBottom: space.medium + (isCompact ? insets.bottom : 0),
    }),
    [insets.bottom, isCompact, space.medium]
  );

  return (
    <>
      <Animated.FlatList
        contentContainerStyle={containerStyle}
        data={edges}
        renderItem={renderItem}
        ItemSeparatorComponent={AHSeparator}
        keyExtractor={(item) => item.node.id}
        onEndReachedThreshold={0.6}
        onEndReached={() => {
          if (hasNext && !isLoadingNext) {
            loadNext(20);
          }
        }}
        ListFooterComponent={
          isLoadingNext ? (
            <Box spaceTop="medium">
              <ActivityIndicator size="large" />
            </Box>
          ) : null
        }
        itemLayoutAnimation={defaultLayoutTransition}
      />
    </>
  );
}

export const TodoCategoryScreen = withWebModalStack(
  (props: TodoCategoryScreenProps) => {
    useNavigationHeader({
      type: "CLOSE",
      disableBorder: true,
      title: props.route.params.title,
      onClose: props.navigation.goBack,
      defaultContentStyle: true,
    });
    const render = useCallback(
      (fetchKey: string) => (
        <TodoCategoryScreenContent {...props} fetchKey={fetchKey} />
      ),
      [props]
    );
    return <LazyQueryNetworkBoundrary>{render}</LazyQueryNetworkBoundrary>;
  },
  true
);

function TodoCategoryScreenContent({
  navigation,
  route: { params },
  fetchKey,
}: TodoCategoryScreenProps & { fetchKey: string }) {
  const [selected, _setSelected] = useState<"ALL" | "ME">("ALL");
  const setSelected = useCallback((val: "ALL" | "ME") => {
    _setSelected(val);
  }, []);

  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const deferredSelectedTagIds = useDeferredValue(selectedTagIds);

  const { currentEmployeeId } = usePersistedEmployeeId();

  const getParams = useCallback(
    (mode: "ME" | "ALL") => ({
      assignedToMe: mode === "ME",
      assignedToEmployeeId:
        mode === "ME" ? (currentEmployeeId ?? undefined) : undefined,
    }),
    [currentEmployeeId]
  );

  const data = useLazyLoadQuery<TodoCategoryScreenQuery>(
    TODO_CATEGORY_SCREEN_QUERY,
    {
      id: params.id,
      teamId: params.teamId,
      tagIds: deferredSelectedTagIds.length > 0 ? deferredSelectedTagIds : null,
      employeeId: currentEmployeeId,
      ...getParams(selected),
      first: 10,
    },
    { fetchPolicy: "store-and-network", fetchKey }
  );

  const [openedAt, setOpenedAt] = useState(new Date().toISOString());

  useFocusEffect(
    useCallback(() => {
      setOpenedAt(new Date().toISOString());
    }, [])
  );

  useNotifyVisitedObjectAtLeaveNav("todoCategory", params.teamId, params.id);

  const me = currentEmployeeId ?? data.currentStore?.currentEmployee?.id;
  const onRequestCreate = useCallback(() => {
    navigation.navigate("TodoCreate", {
      categoryId: params.id,
      title: params.title,
      teamIds: [params.teamId],
      initialAssignedToMe: selected === "ME" ? me : undefined,
      connectionId: ConnectionHandler.getConnectionID(
        data.todoCategory.team.id,
        "TodoCategoryScreen_todos",
        getParams("ALL")
      ),
      assignedToMeConnectionId: ConnectionHandler.getConnectionID(
        data.todoCategory.team.id,
        "TodoCategoryScreen_todos",
        getParams("ME")
      ),
    });
  }, [
    data.todoCategory.team.id,
    getParams,
    me,
    navigation,
    params.id,
    params.teamId,
    params.title,
    selected,
  ]);

  useHotkeys("n", onRequestCreate, {
    preventDefault: true,
    enabled: !data.todoCategory.integration,
  });
  const theme = useTheme();

  const openTodo = useCallback(
    (fromResponse: boolean, todoId: string) => {
      navigation.navigate("TodoEdit", {
        todoId,
        autofocusResponse: fromResponse,
      });
    },
    [navigation]
  );

  return (
    <CurrentTeamIdProvider value={params.teamId}>
      <Box style={{ flex: 1 }}>
        <TodoCategoryScreenFilter
          todoCategory={data.todoCategory}
          selectedTagIds={selectedTagIds}
          setSelectedTagIds={setSelectedTagIds}
          onSetAssignedToMe={(isAssignedToMe) => {
            setSelected(isAssignedToMe ? "ME" : "ALL");
          }}
          isAssignedToMe={selected === "ME"}
          employee={data.currentStore?.currentEmployee ?? null}
        />

        <Suspense fallback={<AHQueryHandlerLoading />}>
          <TodoCategoryList
            team={data.todoCategory.team}
            openedAt={openedAt}
            teamId={params.teamId}
            todoCategoryId={params.id}
            EmptyComponent={
              <NoTodosComponent
                categoryTitle={params.title}
                mode={selected}
                color={data?.todoCategory.team.team?.color ?? theme.primary}
                teamName={data?.todoCategory.team.team?.name ?? ""}
              />
            }
            openTodo={openTodo}
          />
        </Suspense>

        {data.currentStore?.myRole !== "READ_ONLY" &&
        !data.todoCategory.integration ? (
          <ActionFooter
            safeInsetBottom
            primary={
              <ActionFooter.Primary
                title={
                  <FormattedMessage
                    defaultMessage="Add"
                    description="TodoCategoryScreen:AddTodoButton:title"
                  />
                }
                icon="add"
                onPress={onRequestCreate}
              />
            }
          />
        ) : null}
      </Box>
    </CurrentTeamIdProvider>
  );
}
